import { VitalsSummary } from "@/routes/dashboard/types/alert-row-types";
import {
  ChfSymptomLinkId,
  LOINC_SYSTEM,
  VITALL_MESSAGE_ORDER_SYSTEM,
  VITALL_MESSAGE_TYPE_SYSTEM,
  VitalInterpretationCode,
  YesNoCode,
} from "@/utils/fhir-constants";
import { extractMessagesFromAlgorithmResult, getInterpretationCode } from "@/utils/fhir-observation-utils";
import { FhirResourceType } from "@/utils/fhir-utils";
import { ENDS_WITH_PUNCTUATION } from "@/utils/form-validators";
import {
  DEFAULT_WEIGHT_UNIT,
  type WeightUnit,
  isWeightUnit,
  readingToUnit,
  weightUnitLabelKey,
} from "@/utils/weight-units";

import { fhirR4 } from "@smile-cdr/fhirts";
import { LOINC } from "@vitallhealth/fhir-core";

// LOINC codes for vitals
export const LOINC_WEIGHT = LOINC.BODY_WEIGHT.code;
export const LOINC_WEIGHT_MEASURED = LOINC.BODY_WEIGHT_MEASURED.code;
export const LOINC_HEART_RATE = "8867-4";
export const LOINC_BLOOD_PRESSURE = "85354-9";
export const LOINC_SYSTOLIC = "8480-6";
export const LOINC_DIASTOLIC = "8462-4";

/**
 * Extracts a vitals summary from a list of Observation resources.
 * Looks for Weight, Heart Rate, and Blood Pressure observations by LOINC code.
 * Weight is shown in `weightUnit` whatever unit the reading was stored in.
 */
export const extractVitalsSummary = (
  observations: fhirR4.Observation[],
  weightUnit: WeightUnit = DEFAULT_WEIGHT_UNIT
): VitalsSummary => {
  const summary: VitalsSummary = {};

  const hasLoincCode = (obs: fhirR4.Observation, loincCode: string): boolean =>
    obs.code?.coding?.some(c => c.system === LOINC_SYSTEM && c.code === loincCode) ?? false;

  const isWeightObs = (obs: fhirR4.Observation): boolean =>
    hasLoincCode(obs, LOINC_WEIGHT) || hasLoincCode(obs, LOINC_WEIGHT_MEASURED);

  for (const obs of observations) {
    if (isWeightObs(obs)) {
      const weightValue = readingToUnit(obs.valueQuantity, weightUnit);
      const interpretationCode = getInterpretationCode(obs.interpretation);
      // Determine which LOINC code this observation uses
      const loincCode = hasLoincCode(obs, LOINC_WEIGHT) ? LOINC_WEIGHT : LOINC_WEIGHT_MEASURED;
      summary.weight = {
        value: weightValue != null ? weightValue.toFixed(1) : "-",
        // The unit code; translateVitalsSummary turns it into the display label
        unit: weightUnit,
        interpretation: interpretationCode ?? "",
        interpretationCode,
        loincCode,
      };
    }

    if (hasLoincCode(obs, LOINC_HEART_RATE)) {
      const interpretationCode = getInterpretationCode(obs.interpretation);
      summary.heartRate = {
        value: obs.valueQuantity?.value?.toString() ?? "-",
        // The stored unit; translateVitalsSummary turns it into the display label
        unit: obs.valueQuantity?.unit ?? "bpm",
        interpretation: interpretationCode ?? "",
        interpretationCode,
        loincCode: LOINC_HEART_RATE,
      };
    }

    if (hasLoincCode(obs, LOINC_BLOOD_PRESSURE)) {
      const systolic = obs.component?.find(c => c.code?.coding?.some(cd => cd.code === LOINC_SYSTOLIC));
      const diastolic = obs.component?.find(c => c.code?.coding?.some(cd => cd.code === LOINC_DIASTOLIC));

      const sysValue = systolic?.valueQuantity?.value?.toString() ?? "-";
      const diaValue = diastolic?.valueQuantity?.value?.toString() ?? "-";

      const interpretationCode = getInterpretationCode(obs.interpretation);
      summary.bloodPressure = {
        value: `${sysValue}/${diaValue}`,
        unit: systolic?.valueQuantity?.unit ?? "mmHg",
        interpretation: interpretationCode ?? "",
        interpretationCode,
        loincCode: LOINC_BLOOD_PRESSURE,
      };
    }
  }

  return summary;
};

/**
 * Translates raw interpretation codes in a VitalsSummary into display labels.
 * Weight interpretation code "A" is translated as "Large Delta" via a weight-specific key;
 * all other vitals use the generic vitalInterpretation translation.
 */
export const translateVitalsSummary = (
  rawSummary: VitalsSummary,
  translate: (key: string, fallback: string) => string
): VitalsSummary => {
  const mapInterpretation = (code: string) =>
    code ? translate(`pages.dashboard.vitalInterpretation.${code}`, code) : "";

  const mapWeightInterpretation = (code: string) =>
    code ? translate(`pages.dashboard.vitalInterpretation.${code}_weight`, mapInterpretation(code)) : "";

  return {
    weight: rawSummary.weight
      ? {
          ...rawSummary.weight,
          unit: isWeightUnit(rawSummary.weight.unit)
            ? translate(weightUnitLabelKey(rawSummary.weight.unit), rawSummary.weight.unit)
            : rawSummary.weight.unit,
          interpretation: mapWeightInterpretation(rawSummary.weight.interpretation),
        }
      : undefined,
    heartRate: rawSummary.heartRate
      ? {
          ...rawSummary.heartRate,
          unit: KNOWN_HEART_RATE_UNITS.has(rawSummary.heartRate.unit.trim())
            ? translate(HEART_RATE_UNIT_LABEL_KEY, "bpm")
            : rawSummary.heartRate.unit,
          interpretation: mapInterpretation(rawSummary.heartRate.interpretation),
        }
      : undefined,
    bloodPressure: rawSummary.bloodPressure
      ? { ...rawSummary.bloodPressure, interpretation: mapInterpretation(rawSummary.bloodPressure.interpretation) }
      : undefined,
  };
};

/**
 * Stored heart-rate units the display label applies to. Readings store the
 * Vitall canonical "beats/minute" (older records carry "{beats}/min" or
 * "bpm"); clinicians read the translated "bpm" label. A unit outside this
 * set is shown as stored rather than relabelled on a guess.
 */
const KNOWN_HEART_RATE_UNITS = new Set(["beats/minute", "{beats}/min", "bpm"]);
const HEART_RATE_UNIT_LABEL_KEY = "common.units.heartRate";

const MESSAGE_COMPONENT_TEXT = "Message attributes";

/** Patient-facing reading status (e.g. "Your weight is high."); stored on the record but not shown to clinicians. */
const STATUS_MESSAGE_TYPE = "STATUS";

const isStatusMessage = (component: fhirR4.ObservationComponent): boolean =>
  component.code?.coding?.some(c => c.system === VITALL_MESSAGE_TYPE_SYSTEM && c.code === STATUS_MESSAGE_TYPE) ?? false;

const messageOrder = (component: fhirR4.ObservationComponent): number =>
  parseInt(component.code?.coding?.find(c => c.system === VITALL_MESSAGE_ORDER_SYSTEM)?.code ?? "0");

const ensureTerminalPunctuation = (text: string): string => (ENDS_WITH_PUNCTUATION.test(text) ? text : `${text}.`);

/**
 * Clinician-facing messages from an algorithm result Observation, shared by the alert row and detail card.
 * Reads `note[]` first, else message components sorted by messageOrder with STATUS excluded.
 */
export const extractAlertMessages = (observation: fhirR4.Observation): string[] => {
  const noteMessages = extractMessagesFromAlgorithmResult(observation);
  if (noteMessages.length > 0) {
    return noteMessages.map(ensureTerminalPunctuation);
  }

  const messageComponents =
    observation.component?.filter(c => c.code?.text === MESSAGE_COMPONENT_TEXT && !isStatusMessage(c)) ?? [];

  return [...messageComponents]
    .sort((a, b) => messageOrder(a) - messageOrder(b))
    .map(c => c.valueString)
    .filter((text): text is string => !!text)
    .map(ensureTerminalPunctuation);
};

export interface SymptomSummaryItem {
  linkId: string;
  value?: number;
}

/**
 * Maps weight interpretation codes to colors.
 * Follows a severity-based color scheme:
 * - Normal → no color
 * - Mildly abnormal (L, H, H1, A, AM, AU) → orange
 * - Moderately abnormal (LU, HU) → orange
 * - Critically abnormal (LL, HH) → red
 */
export const mapWeightReadingToColor = (interpretationCode: string | undefined): string | undefined => {
  if (!interpretationCode || interpretationCode === VitalInterpretationCode.NORMAL) {
    return undefined;
  }

  // Critically abnormal
  if (
    interpretationCode === VitalInterpretationCode.CRITICALLY_LOW ||
    interpretationCode === VitalInterpretationCode.CRITICALLY_HIGH
  ) {
    return "error.dark";
  }

  // Moderately or mildly abnormal → orange
  return "alertOrange.dark";
};

/**
 * Maps heart rate interpretation codes to colors.
 * Same logic as weight readings.
 */
export const mapHeartRateReadingToColor = (interpretationCode: string | undefined): string | undefined => {
  if (!interpretationCode || interpretationCode === VitalInterpretationCode.NORMAL) {
    return undefined;
  }

  // Critically abnormal
  if (
    interpretationCode === VitalInterpretationCode.CRITICALLY_LOW ||
    interpretationCode === VitalInterpretationCode.CRITICALLY_HIGH
  ) {
    return "error.dark";
  }

  // Moderately or mildly abnormal → orange
  return "alertOrange.dark";
};

/**
 * Maps blood pressure interpretation codes to colors.
 * Special handling: very low and critically low BP are both red (urgent)
 * because low BP is more dangerous than high BP.
 */
export const mapBloodPressureReadingToColor = (interpretationCode: string | undefined): string | undefined => {
  if (!interpretationCode || interpretationCode === VitalInterpretationCode.NORMAL) {
    return undefined;
  }

  // Critically or very low BP → urgent (red)
  if (
    interpretationCode === VitalInterpretationCode.CRITICALLY_LOW ||
    interpretationCode === VitalInterpretationCode.VERY_LOW
  ) {
    return "error.dark";
  }

  // Critically high BP
  if (interpretationCode === VitalInterpretationCode.CRITICALLY_HIGH) {
    return "error.dark";
  }

  // Very high BP or mildly abnormal → orange
  return "alertOrange.dark";
};

/**
 * Determines the color for a biometric value based on its LOINC code and interpretation.
 * Uses observation-specific color mapping functions.
 * Only colors when backend provides interpretation code - no fallback derivation.
 *
 * @param loincCode - LOINC code identifying the observation type
 * @param interpretationCode - FHIR interpretation code (N, L, H, LL, LU, etc.)
 * @returns MUI palette color path or undefined for default color
 */
export const getBiometricColor = (
  loincCode: string | undefined,
  interpretationCode: string | undefined
): string | undefined => {
  if (!loincCode) {
    return undefined;
  }

  // Weight observations
  if (loincCode === LOINC_WEIGHT || loincCode === LOINC_WEIGHT_MEASURED) {
    return mapWeightReadingToColor(interpretationCode);
  }

  // Heart rate observations
  if (loincCode === LOINC_HEART_RATE) {
    return mapHeartRateReadingToColor(interpretationCode);
  }

  // Blood pressure observations
  if (loincCode === LOINC_BLOOD_PRESSURE) {
    return mapBloodPressureReadingToColor(interpretationCode);
  }

  return undefined;
};

/**
 * Extracts symptom summary items from a QuestionnaireResponse contained within an array of resources.
 * Recurses into nested group items. Returns items where the patient answered "yes"
 * and numeric answers like number_of_pillows (when valueInteger > 0).
 */
export const extractSymptomSummary = (contained: fhirR4.Resource[]): SymptomSummaryItem[] => {
  const qr = contained?.find((r: fhirR4.Resource) => r.resourceType === FhirResourceType.QuestionnaireResponse) as
    fhirR4.QuestionnaireResponse | undefined;

  if (!qr?.item) {
    return [];
  }

  const result: SymptomSummaryItem[] = [];

  const collect = (item: fhirR4.QuestionnaireResponseItem) => {
    if (item.answer?.[0]?.valueCoding?.code?.toUpperCase() === YesNoCode.Yes) {
      if (item.linkId && item.linkId !== ChfSymptomLinkId.NumberOfPillows) {
        result.push({ linkId: item.linkId });
      }
    }

    const intValue = item.answer?.[0]?.valueInteger;
    if (item.linkId === ChfSymptomLinkId.NumberOfPillows && intValue != null && intValue > 0) {
      result.push({ linkId: item.linkId, value: intValue });
    }

    item.item?.forEach(collect);
  };

  qr.item.forEach(collect);
  return result;
};
